(() => {
  'use strict';
  if (customElements.get('broek-archive')) return;
  const SIZES = ['M', 'L', 'XL', 'XXL'];
  const esc = (value) => String(value).replace(/[&<>"']/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  const money = (cents, currency) => currency === 'EGP' ? 'LE ' + (cents / 100).toLocaleString('en-EG', {maximumFractionDigits:2}) : (cents / 100).toLocaleString('en', {style:'currency',currency});
  const photo = (source, width) => {
    const url = new URL(source, window.location.origin);
    if (url.protocol !== 'https:' || (url.hostname !== 'cdn.shopify.com' && url.origin !== window.location.origin)) return '';
    url.searchParams.set('width', String(width));
    return url.toString();
  };
  class BroekArchive extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.products = [];
      this.limit = 24;
      this.state = {size:'', type:'all'};
      this.dialog = this.querySelector('dialog');
      this.addEventListener('click', (event) => this.onClick(event));
      this.onPopState = () => {this.readUrl();this.render(false);};
      window.addEventListener('popstate', this.onPopState);
      this.dialog.addEventListener('click', (event) => {if (event.target === this.dialog) this.dialog.close();});
      this.dialog.addEventListener('close', () => {if (this.opener && this.opener.isConnected) this.opener.focus({preventScroll:true});});
      this.addEventListener('error', (event) => {
        if (event.target.tagName !== 'IMG') return;
        const image = event.target;
        image.hidden = true;
        const fallback = document.createElement('span');
        fallback.className = 'ba__image-fallback';
        fallback.textContent = 'Image unavailable';
        image.parentElement.append(fallback);
      }, true);
      this.load();
    }
    disconnectedCallback() {window.removeEventListener('popstate', this.onPopState);}
    async load() {
      const status = this.querySelector('[data-load-status]');
      const retry = this.querySelector('[data-action="retry"]');
      status.hidden = false;
      status.textContent = 'Loading size filters…';
      retry.hidden = true;
      try {
        const source = new URL(this.dataset.source, window.location.origin);
        const previewTheme = new URL(window.location.href).searchParams.get('preview_theme_id');
        if (previewTheme) source.searchParams.set('preview_theme_id',previewTheme);
        const products = new Map();
        let page = 1;
        let pages = 1;
        do {
          source.searchParams.set('page',String(page));
          const response = await fetch(source, {credentials:'same-origin',cache:'no-store',signal:AbortSignal.timeout(20000)});
          if (!response.ok) throw new Error('Archive data unavailable');
          const document = new DOMParser().parseFromString(await response.text(),'text/html');
          const content = document.querySelector('[data-archive-live]');
          const data = content && JSON.parse(content.textContent);
          if (!data || data.mode !== 'live' || !Array.isArray(data.products) || data.page !== page || !Number.isInteger(data.pages)) throw new Error('Invalid archive data');
          this.currency = data.currency || 'EGP';
          data.products.forEach((product) => products.set(product.handle,product));
          pages = data.pages;
          if (pages > 100) throw new Error('Archive page limit exceeded');
          page += 1;
        } while (page <= pages);
        this.products = Array.from(products.values()).filter((p) => p.images && p.images.length && Array.isArray(p.variants)).map((p) => ({...p, variants:p.variants.filter((v) => SIZES.includes(v.size) && v.tracked && v.available && v.quantity > 0 && v.price > 0)})).filter((p) => p.variants.length);
        this.products.forEach((p) => {p.stock = p.variants.reduce((sum, v) => sum + v.quantity, 0);});
        this.products.sort((a,b) => b.stock - a.stock || b.variants.length - a.variants.length || a.title.localeCompare(b.title,'en'));
        this.querySelectorAll('[data-size]').forEach((button) => {
          const count = this.products.filter((p) => this.variant(p, button.dataset.size)).length;
          button.disabled = count === 0;
          button.setAttribute('aria-label', button.dataset.size + ' — ' + count + ' available styles');
        });
        const maximumDiscount = Math.max(0, ...this.products.flatMap((p) => p.variants.map((v) => v.compareAtPrice > v.price ? (1 - v.price / v.compareAtPrice) * 100 : 0)));
        const offer = this.querySelector('[data-archive-offer]');
        offer.textContent = 'Up to ' + Math.round(maximumDiscount) + '% off';
        offer.hidden = maximumDiscount < 1;
        this.loaded = true;
        this.querySelectorAll('[data-type]').forEach((control) => {control.disabled = false;});
        this.querySelector('[data-loading-panel]').hidden = true;
        status.hidden = true;
        this.readUrl();
        this.render(false);
      } catch (error) {
        status.textContent = 'Size filters could not load. You can still browse the pieces below, or try again.';
        retry.hidden = false;
      }
    }
    variant(product, size = this.state.size) {return product.variants.find((v) => (!size || v.size === size) && v.quantity > 0 && v.price > 0);}
    readUrl() {
      const q = new URL(window.location.href).searchParams;
      const size = q.get('archive_size');
      this.state.size = SIZES.includes(size) ? size : '';
      this.state.type = ['hoodie','sweatshirt'].includes(q.get('archive_type')) ? q.get('archive_type') : 'all';
      this.syncUrl();
      this.limit = 24;
    }
    syncUrl() {
      const url = new URL(window.location.href);
      const values = {archive_size:this.state.size, archive_type:this.state.type === 'all' ? '' : this.state.type};
      Object.entries(values).forEach(([key,value]) => value ? url.searchParams.set(key,value) : url.searchParams.delete(key));
      url.searchParams.delete('archive_q');
      url.searchParams.delete('archive_sort');
      history.replaceState(history.state, '', url);
    }
    render(updateUrl = true) {
      if (!this.loaded) return;
      const selected = Boolean(this.state.size);
      if (updateUrl) this.syncUrl();
      this.querySelector('[data-action="reset"]').hidden = !selected;
      this.querySelectorAll('[data-size]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.size === this.state.size)));
      this.querySelectorAll('[data-type]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.type === this.state.type)));
      const filtered = this.products.filter((p) => this.variant(p) && (this.state.type === 'all' || p.type === this.state.type));
      if (selected) filtered.sort((a,b) => this.variant(b).quantity - this.variant(a).quantity);
      this.querySelector('[data-result-count]').textContent = filtered.length + (filtered.length === 1 ? ' style' : ' styles') + (selected ? ' available in ' + this.state.size : ' available');
      this.querySelector('[data-grid]').innerHTML = filtered.slice(0,this.limit).map((p) => this.card(p)).join('');
      this.querySelector('[data-empty]').hidden = filtered.length !== 0;
      this.querySelector('[data-progress]').textContent = filtered.length ? 'Showing ' + Math.min(this.limit,filtered.length) + ' of ' + filtered.length + ' styles' : '';
      this.querySelector('[data-action="more"]').hidden = this.limit >= filtered.length;
    }
    priceMarkup(variant) {
      return '<span><span class="ba__sr">Current price </span>' + money(variant.price,this.currency) + '</span>' +
        (variant.compareAtPrice > variant.price ? '<s><span class="ba__sr">Previous price </span>' + money(variant.compareAtPrice,this.currency) + '</s>' : '');
    }
    card(product) {
      const v = this.variant(product);
      const template = document.createElement('template');
      template.innerHTML = product.card;
      const card = template.content.querySelector('.bv3-card');
      if (!card) return '';
      card.classList.add('ba__card');
      card.dataset.handle = product.handle;
      card.dataset.availableSize = v.size;
      card.dataset.price = v.price;
      card.dataset.compareAtPrice = v.compareAtPrice || '';
      const productUrl = new URL(product.url,window.location.origin);
      productUrl.searchParams.set('variant',String(v.id));
      card.querySelectorAll('.bv3-card-media > a, .bv3-card-title').forEach((link) => {link.href = productUrl.pathname + productUrl.search;});
      card.querySelectorAll('.bv3-colours a').forEach((link) => {
        const url = new URL(link.getAttribute('href'),window.location.origin);
        const sibling = this.products.find((p) => new URL(p.url,window.location.origin).pathname === url.pathname);
        const siblingVariant = sibling && this.variant(sibling);
        if (!siblingVariant) {link.remove();return;}
        url.searchParams.set('variant',String(siblingVariant.id));
        link.href = url.pathname + url.search;
        link.removeAttribute('data-bv3-swatch');
      });
      card.querySelector('.bv3-card-price').innerHTML = this.priceMarkup(v);
      card.querySelectorAll('.bv3-printed-saving,.bv3-card-offer').forEach((element) => element.remove());
      const quickview = card.querySelector('button-quickview');
      if (quickview) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = quickview.className;
        button.dataset.open = product.handle;
        button.setAttribute('aria-label','Quick view: ' + product.title);
        button.innerHTML = quickview.innerHTML;
        quickview.replaceWith(button);
      }
      return card.outerHTML;
    }
    onClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button) || button.disabled) return;
      if (button.dataset.size) {this.state.size = button.dataset.size;this.limit = 24;this.render();return;}
      if (button.dataset.type) {this.state.type = button.dataset.type;this.limit = 24;this.render();return;}
      if (button.dataset.open) {
        const product = this.products.find((p) => p.handle === button.dataset.open);
        if (!product || !this.variant(product)) return;
        this.opener = button;
        this.detailProduct = product;
        this.detailSize = this.variant(product).size;
        this.renderDetail();
        this.dialog.showModal();
        this.querySelector('[data-action="close"]').focus({preventScroll:true});
        return;
      }
      if (button.dataset.detailSize) {this.detailSize = button.dataset.detailSize;this.renderDetailCopy();return;}
      if (button.dataset.photo !== undefined) {
        const index = Number(button.dataset.photo);
        const image = this.querySelector('[data-detail-image]');
        image.hidden = false;
        image.src = photo(this.detailProduct.images[index],1000);
        image.alt = this.detailProduct.title + ' — image ' + (index + 1);
        this.querySelectorAll('[data-photo]').forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
        return;
      }
      switch (button.dataset.action) {
        case 'reset':this.state.size = '';this.limit = 24;this.render();this.querySelector('[data-size="M"]').focus({preventScroll:true});break;
        case 'clear-filters':this.state.type = 'all';this.limit = 24;this.render();break;
        case 'more':{const oldLimit = this.limit;this.limit += 24;this.render();const firstNew = this.querySelectorAll('[data-grid] .bv3-card-title')[oldLimit];if (firstNew) firstNew.focus({preventScroll:true});break;}
        case 'close':this.dialog.close();break;
        case 'retry':this.load();break;
      }
    }
    renderDetail() {
      const p = this.detailProduct;
      const title = esc(p.title);
      const thumbs = p.images.map((url,index) => '<button type="button" data-photo="' + index + '" aria-label="View image ' + (index + 1) + '" aria-pressed="' + (index === 0) + '"><img src="' + esc(photo(url,120)) + '" alt="" width="52" height="66" loading="lazy"></button>').join('');
      this.querySelector('[data-detail]').innerHTML = '<div class="ba__detail-layout"><div class="ba__detail-media"><img class="ba__detail-image" data-detail-image src="' + esc(photo(p.images[0],1000)) + '" alt="' + title + '" width="560" height="700"><div class="ba__thumbs">' + thumbs + '</div></div><div class="ba__detail-copy"><p class="ba__eyebrow">WINTER SALE</p><h2 id="ba-detail-title">' + title + '</h2><div data-detail-copy></div></div></div>';
      this.dialog.setAttribute('aria-labelledby','ba-detail-title');
      this.renderDetailCopy();
    }
    renderDetailCopy() {
      const p = this.detailProduct;
      const v = this.variant(p,this.detailSize);
      if (!v) return;
      const sizes = SIZES.map((size) => '<button type="button" data-detail-size="' + size + '" aria-pressed="' + (size === this.detailSize) + '" ' + (!this.variant(p,size) ? 'disabled' : '') + '>' + size + '</button>').join('');
      const productUrl = new URL(p.url,window.location.origin);
      productUrl.searchParams.set('variant',String(v.id));
      this.querySelector('[data-detail-copy]').innerHTML = '<p class="ba__detail-price">' + this.priceMarkup(v) + '</p><span class="ba__detail-label">Available sizes</span><div class="ba__detail-sizes" role="group" aria-label="Piece size">' + sizes + '</div><p class="ba__detail-stock" role="status">' + v.quantity + (v.quantity === 1 ? ' piece' : ' pieces') + ' available in ' + v.size + '</p><p class="ba__detail-note">An original piece from a previous BROEK collection. Explore the original product photos before choosing.</p>' +
        '<form class="ba__buy-form" method="post" action="' + esc(this.dataset.cartAdd) + '"><input type="hidden" name="id" value="' + v.id + '"><input type="hidden" name="quantity" value="1"><input type="hidden" name="return_to" value="' + esc(this.dataset.cart) + '"><button class="ba__add" type="submit">' + esc(this.dataset.addLabel) + ' · ' + v.size + '</button></form>' +
        '<a class="ba__product-link" href="' + esc(productUrl.pathname + productUrl.search) + '">View full product details ↗</a>';
    }
  }
  customElements.define('broek-archive',BroekArchive);
})();
